/**
 * PostClosingProgress Component
 *
 * 7-step compact dot stepper showing post-closing workflow progression.
 * Two variants: compact (table cell) and expanded (detail sheet).
 */

import { useMemo } from 'react';
import { Check, CheckCircle2, Circle, Minus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { ProspectLoan } from './types';

// ── Stage Definitions ─────────────────────────────────────────────────────────

export const POST_CLOSING_STAGES = [
  { key: 'funded',        label: 'Funded',        shortLabel: 'Fund' },
  { key: 'ready_to_ship', label: 'Ready to Ship',  shortLabel: 'RTS' },
  { key: 'delivered',     label: 'Delivered',      shortLabel: 'Dlvd' },
  { key: 'conditions',    label: 'Conditions',     shortLabel: 'Cond' },
  { key: 'cleared',       label: 'Cleared',        shortLabel: 'Clrd' },
  { key: 'pa_issued',     label: 'PA Issued',      shortLabel: 'PA' },
  { key: 'finalized',     label: 'Finalized',      shortLabel: 'Final' }
] as const;

export type StageState = 'complete' | 'current' | 'pending' | 'skipped';

export interface StageResult {
  key: string;
  label: string;
  state: StageState;
  date?: string;
}

// ── Fields needed for stage computation ───────────────────────────────────────

type StageInputFields = Pick<ProspectLoan,
  'funded_date' | 'ready_to_ship_date' | 'shipping_investor_delivery_date' |
  'investor_conditions_status' | 'conditions_outstanding_at' | 'conditions_cleared_at' |
  'first_pa_date' | 'recon_status' | 'recon_finalized_at'
>;

// ── Pure stage computation (exported for testing) ─────────────────────────────

export function computeStageStates(loan: StageInputFields): StageResult[] {
  const condStatus = loan.investor_conditions_status || 'none';

  // Step 1: Build completion flags
  const completionFlags: boolean[] = [
    !!loan.funded_date,                                          // funded
    !!loan.ready_to_ship_date,                                   // ready_to_ship
    !!loan.shipping_investor_delivery_date,                      // delivered
    condStatus === 'outstanding' || condStatus === 'cleared',    // conditions
    condStatus === 'cleared',                                    // cleared
    !!loan.first_pa_date,                                        // pa_issued
    loan.recon_status === 'finalized' || loan.recon_status === 'pushed' // finalized
  ];

  // Step 2: Build skipped flags — must happen BEFORE current-stage walk
  // Stages 4-5 are skipped when conditions were never tracked but later stages exist
  const laterStagesComplete = completionFlags[5] || completionFlags[6]; // PA or finalized
  const conditionsNeverTracked = condStatus === 'none';
  const skippedFlags: boolean[] = [
    false, // funded
    false, // ready_to_ship
    false, // delivered
    conditionsNeverTracked && laterStagesComplete, // conditions
    conditionsNeverTracked && laterStagesComplete, // cleared
    false, // pa_issued
    false // finalized
  ];

  // Step 3: Build dates for tooltip display
  const dates: (string | undefined)[] = [
    loan.funded_date || undefined,
    loan.ready_to_ship_date || undefined,
    loan.shipping_investor_delivery_date || undefined,
    loan.conditions_outstanding_at || undefined,
    loan.conditions_cleared_at || undefined,
    loan.first_pa_date || undefined,
    loan.recon_finalized_at || undefined
  ];

  // Step 4: Walk left-to-right, skipping skipped stages for current selection
  let foundCurrent = false;
  return POST_CLOSING_STAGES.map((stage, i) => {
    let state: StageState;
    if (skippedFlags[i]) {
      state = 'skipped';
    } else if (completionFlags[i]) {
      state = 'complete';
    } else if (!foundCurrent) {
      state = 'current';
      foundCurrent = true;
    } else {
      state = 'pending';
    }
    return { key: stage.key, label: stage.label, state, date: dates[i] };
  });
}

// ── Date formatter for tooltip ────────────────────────────────────────────────

function formatStageDate(dateStr: string | undefined): string {
  if (!dateStr) {
return '';
}
  try {
    const date = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
    if (isNaN(date.getTime())) {
return '';
}
    return format(date, 'M/d/yy');
  } catch {
    return '';
  }
}

// ── Get last cleared stage label ──────────────────────────────────────────────

function getLastClearedStageLabel(stages: StageResult[]): string {
  const allDone = stages.every(s => s.state === 'complete' || s.state === 'skipped');
  if (allDone) {
return 'Complete';
}
  // Walk backward to find the last completed stage
  for (let i = stages.length - 1; i >= 0; i--) {
    if (stages[i].state === 'complete') {
return stages[i].label;
}
  }
  return 'Pending';
}

// ── Tooltip content ───────────────────────────────────────────────────────────

function StageTooltipContent({ stages }: { stages: StageResult[] }) {
  return (
    <div className="space-y-1 py-1">
      {stages.map((stage) => {
        const dateStr = formatStageDate(stage.date);
        return (
          <div key={stage.key} className="flex items-center gap-2 text-xs">
            <span className="w-3 flex-shrink-0">
              {stage.state === 'complete' ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : stage.state === 'current' ? (
                <span className="text-blue-500 font-bold">&rarr;</span>
              ) : stage.state === 'skipped' ? (
                <Minus className="h-3 w-3 text-gray-400" />
              ) : (
                <span className="text-gray-400">&nbsp;</span>
              )}
            </span>
            <span className={cn(
              'flex-1',
              stage.state === 'complete' && 'text-foreground',
              stage.state === 'current' && 'text-blue-500 font-medium',
              stage.state === 'skipped' && 'text-muted-foreground line-through',
              stage.state === 'pending' && 'text-muted-foreground'
            )}>
              {stage.label}
            </span>
            {dateStr && (
              <span className="text-muted-foreground ml-2">{dateStr}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Compact variant (table cell) ──────────────────────────────────────────────

function CompactStepper({ stages }: { stages: StageResult[] }) {
  const currentLabel = getLastClearedStageLabel(stages);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-[3px]">
        {stages.map((stage, i) => (
          <div key={stage.key} className="flex items-center">
            {/* Dot */}
            <div
              className={cn(
                'w-2 h-2 rounded-full flex-shrink-0 transition-colors',
                stage.state === 'complete' && 'bg-green-500',
                stage.state === 'current' && 'bg-blue-500 animate-pulse',
                stage.state === 'pending' && 'bg-gray-300',
                stage.state === 'skipped' && 'bg-gray-300'
              )}
            />
            {/* Connecting line (not after last dot) */}
            {i < stages.length - 1 && (
              <div
                className={cn(
                  'w-[6px] h-[1.5px] flex-shrink-0',
                  stage.state === 'complete' && stages[i + 1].state === 'complete'
                    ? 'bg-green-400'
                    : stage.state === 'complete' && stages[i + 1].state === 'skipped'
                    ? 'bg-gray-300'
                    : 'bg-gray-200'
                )}
              />
            )}
          </div>
        ))}
      </div>
      <span className="text-[10px] text-muted-foreground leading-tight truncate">
        {currentLabel}
      </span>
    </div>
  );
}

// ── Expanded variant (detail sheet) ───────────────────────────────────────────

function ExpandedStepper({ stages }: { stages: StageResult[] }) {
  return (
    <div className="flex items-start justify-between w-full">
      {stages.map((stage, i) => (
        <div key={stage.key} className="flex flex-col items-center flex-1 relative">
          {/* Connecting line */}
          {i < stages.length - 1 && (
            <div
              className={cn(
                'absolute top-[10px] left-[calc(50%+10px)] h-[2px]',
                'right-[calc(-50%+10px)]',
                stage.state === 'complete' && stages[i + 1].state !== 'pending'
                  ? 'bg-green-400'
                  : 'bg-gray-200'
              )}
              style={{ width: 'calc(100% - 20px)', left: 'calc(50% + 10px)' }}
            />
          )}
          {/* Circle */}
          <div
            className={cn(
              'w-5 h-5 rounded-full flex items-center justify-center z-10 transition-colors',
              stage.state === 'complete' && 'bg-green-500 text-white',
              stage.state === 'current' && 'bg-blue-500 text-white ring-2 ring-blue-200',
              stage.state === 'pending' && 'bg-gray-200 text-gray-400',
              stage.state === 'skipped' && 'bg-gray-200 text-gray-400'
            )}
          >
            {stage.state === 'complete' ? (
              <Check className="h-3 w-3" />
            ) : stage.state === 'skipped' ? (
              <Minus className="h-2.5 w-2.5" />
            ) : (
              <Circle className="h-2.5 w-2.5" />
            )}
          </div>
          {/* Label */}
          <span className={cn(
            'text-[10px] mt-1 text-center leading-tight',
            stage.state === 'complete' && 'text-foreground font-medium',
            stage.state === 'current' && 'text-blue-600 font-semibold',
            stage.state === 'pending' && 'text-muted-foreground',
            stage.state === 'skipped' && 'text-muted-foreground line-through'
          )}>
            {stage.label}
          </span>
          {/* Date */}
          {stage.date && (
            <span className="text-[9px] text-muted-foreground">
              {formatStageDate(stage.date)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

interface PostClosingProgressProps {
  loan: StageInputFields;
  variant: 'compact' | 'expanded';
}

export function PostClosingProgress({ loan, variant }: PostClosingProgressProps) {
  const stages = useMemo(() => computeStageStates(loan), [
    loan.funded_date,
    loan.ready_to_ship_date,
    loan.shipping_investor_delivery_date,
    loan.investor_conditions_status,
    loan.conditions_outstanding_at,
    loan.conditions_cleared_at,
    loan.first_pa_date,
    loan.recon_status,
    loan.recon_finalized_at
  ]);

  if (variant === 'expanded') {
    return <ExpandedStepper stages={stages} />;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-default" data-row-interactive="true">
            <CompactStepper stages={stages} />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="p-2">
          <StageTooltipContent stages={stages} />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
